import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { MdAdd } from 'react-icons/md'
import { useSetAtom } from 'jotai'
import { FormActionsFooter } from '../../tailwindComponents/FormActionsFooter'
import { useAuthHttp } from '../../hooks'
import {
  NetworkRateCardZone,
  QuantityBreak,
  PalletDimensions,
  Surcharge,
  ZoneServicePricing,
  NETWORK_RATECARD_CURRENCIES,
  ServiceIdentity,
  DEFAULT_SERVICE,
  PALLET_UNIT,
  createEmptyZone,
  createServicePricing,
  getDefaultPalletCaps,
  getServiceKey,
  getServiceLabel,
  getUniqueServices,
  isPricedSurcharge,
  isSameService,
  lockFootprint,
  removeServiceFromEveryZone,
} from './types'
import { COUNTRIES } from './countries'
import { NetworkZoneGrid } from './NetworkZoneGrid'
import { LocationEditorPanel } from './LocationEditorPanel'
import { QuantityBreaksPanel } from './QuantityBreaksPanel'
import { AddServiceModal } from './AddServiceModal'
import { SurchargesPanel } from './SurchargesPanel'
import { ImportNotesBanner } from './ImportNotesBanner'
import { findProblemBlockingSave, listProblemsToFixAfterImport } from './findProblemBlockingSave'
import { ZoneLocation, applyGeocodeResult, useResolvePostcodes } from './useGeocode'
import { networkRatecardEditorDirtyAtom } from '../../store/jotai'

interface NetworkRatecardEditorProps {
  mode: 'create' | 'edit'
  ratecardId?: string
  initialNetworkName: string
  initialCountryCode: string
  initialCurrency: string
  initialEffectiveFrom: string
  initialEffectiveTo: string
  initialNotes: string
  initialIsActive: boolean
  initialIsLocalRatecard: boolean
  initialZones: NetworkRateCardZone[]
  initialSurcharges: Surcharge[]
  wingmanNotes?: string[]
  saveEndpoint: string
  saveButtonLabel: string
  onSaveSuccess: () => void
  onCancel: () => void
}

export function NetworkRatecardEditor({
  mode,
  ratecardId,
  initialNetworkName,
  initialCountryCode,
  initialCurrency,
  initialEffectiveFrom,
  initialEffectiveTo,
  initialNotes,
  initialIsActive,
  initialIsLocalRatecard,
  initialZones,
  initialSurcharges,
  wingmanNotes,
  saveEndpoint,
  saveButtonLabel,
  onSaveSuccess,
  onCancel,
}: NetworkRatecardEditorProps) {
  const authHttp = useAuthHttp()
  // Dirty-tracking atom — read by the Copilot before firing a second import so it can prompt the
  // user instead of silently nuking their unsaved edits via the remount-key. Reset on mount and
  // on successful save (and on unmount as a defence-in-depth cleanup for navigations away).
  const setEditorDirty = useSetAtom(networkRatecardEditorDirtyAtom)
  useEffect(() => {
    setEditorDirty(false)
    return () => setEditorDirty(false)
  }, [setEditorDirty])
  // Wraps any state setter that represents a user-initiated edit. Mount-time syncs (e.g. the
  // initialZones useEffect) call setZones directly and don't go through this — that's intentional,
  // a prop-driven sync isn't a "dirty" change.
  const markDirty = useCallback(() => setEditorDirty(true), [setEditorDirty])

  // Core state
  const [networkName, setNetworkName] = useState(initialNetworkName)
  const [countryCode, setCountryCode] = useState(initialCountryCode)
  const [currency, setCurrency] = useState(initialCurrency)
  const [effectiveFrom, setEffectiveFrom] = useState(initialEffectiveFrom)
  const [effectiveTo, setEffectiveTo] = useState(initialEffectiveTo)
  const [notes, setNotes] = useState(initialNotes)
  const [isActive, setIsActive] = useState(initialIsActive)
  const [isLocalRatecard, setIsLocalRatecard] = useState(initialIsLocalRatecard)
  const [zones, setZones] = useState<NetworkRateCardZone[]>(initialZones)
  const [surcharges, setSurcharges] = useState<Surcharge[]>(initialSurcharges)

  // Keep local state in sync when `initialZones` changes (e.g. a Copilot import lands while the
  // editor is mounted). Without this, `useState(initialZones)` ignores prop changes and the user
  // sees stale data — the parent's `key={importedData.id}` remount is a backstop for the edge
  // cases this misses (e.g. shape-incompatible re-imports). Skipping the very first run avoids
  // a redundant setZones on mount that would clobber any state-init-side-effects.
  const isFirstZonesSync = useRef(true)
  useEffect(() => {
    if (isFirstZonesSync.current) {
      isFirstZonesSync.current = false
      return
    }
    setZones(initialZones)
  }, [initialZones])

  const isFirstNetworkNameSync = useRef(true)
  useEffect(() => {
    if (isFirstNetworkNameSync.current) {
      isFirstNetworkNameSync.current = false
      return
    }
    setNetworkName(initialNetworkName)
  }, [initialNetworkName])

  const isFirstCountrySync = useRef(true)
  useEffect(() => {
    if (isFirstCountrySync.current) {
      isFirstCountrySync.current = false
      return
    }
    setCountryCode(initialCountryCode)
  }, [initialCountryCode])

  // Mirror the country/zones sync pattern — a Copilot import with an explicit currency in the
  // source PDF needs to land cleanly while the editor is mounted; the parent's `key=...` remount
  // is a backstop. Skip the very first run so we don't clobber the constructor-time initial.
  const isFirstCurrencySync = useRef(true)
  useEffect(() => {
    if (isFirstCurrencySync.current) {
      isFirstCurrencySync.current = false
      return
    }
    setCurrency(initialCurrency)
  }, [initialCurrency])

  // Mirror the zones-sync above for surcharges — a Copilot import landing while the editor is
  // already mounted needs to refresh the panel; the parent's `key={importedData.id}` remount is
  // a backstop. Skip the very first run so we don't clobber the constructor-time initial value.
  const isFirstSurchargesSync = useRef(true)
  useEffect(() => {
    if (isFirstSurchargesSync.current) {
      isFirstSurchargesSync.current = false
      return
    }
    setSurcharges(initialSurcharges)
  }, [initialSurcharges])

  const handleSurchargesChanged = useCallback(
    (next: Surcharge[]) => {
      setSurcharges(next)
      markDirty()
    },
    [markDirty]
  )

  // Panel visibility
  const [selectedZoneForLocation, setSelectedZoneForLocation] = useState<string | null>(null)
  const [selectedPriceCell, setSelectedPriceCell] = useState<{
    zoneId: string
    service: ServiceIdentity
  } | null>(null)
  const [showAddServiceModal, setShowAddServiceModal] = useState(false)

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [hasTriedToSave, setHasTriedToSave] = useState(false)

  const { resolveBatch } = useResolvePostcodes()

  const services = useMemo(() => getUniqueServices(zones), [zones])

  // Count locations the user needs to fix before save can proceed. A location is "unresolved" if
  // it has no geoPoint, regardless of whether it's a Copilot-imported pending row, a backend
  // 'not_found' result, or a transient 'error'. Used by the banner + Save button.
  const unresolvedLocations = useMemo(() => {
    const items: { zoneName: string; prefix: string }[] = []
    for (const z of zones) {
      for (const l of z.locations) {
        if (!l.geoPoint) items.push({ zoneName: z.zoneName, prefix: l.prefix })
      }
    }
    return items
  }, [zones])
  const totalLocations = useMemo(
    () => zones.reduce((sum, z) => sum + z.locations.length, 0),
    [zones]
  )

  const handleLocationsChanged = useCallback((zoneId: string, locations: ZoneLocation[]) => {
    setZones((prev) => prev.map((z) => (z.id === zoneId ? { ...z, locations } : z)))
    markDirty()
  }, [markDirty])

  // Auto-resolve imported zones that came in with prefixes but no coords (Copilot import draft).
  // Runs once per mount. Differences from the previous fire-and-forget Promise.all:
  //   - Promise.allSettled so one zone's network failure doesn't drop the others' results.
  //   - Per-zone try/catch wraps the resolveBatch call (which now throws on bad country codes —
  //     see useGeocode.ts:140) so failure surfaces as a per-row 'error' status, not an unhandled
  //     rejection.
  //   - Tracks `isAutoResolving` + `resolveProgress` so the UI can show a banner and gate Save.
  //   - Marks every location with a `status` flag ('resolved' | 'not_found' | 'error') so the
  //     LocationEditorPanel can render red rows for the user to retry/remove and the save-time
  //     guard can block submission until everything is 'resolved'.
  const [isAutoResolving, setIsAutoResolving] = useState(false)
  const [resolveProgress, setResolveProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  })
  const hasAutoResolved = useRef(false)
  useEffect(() => {
    if (hasAutoResolved.current) return
    const zonesToResolve = initialZones.filter(
      (z) => z.locations.length > 0 && z.locations.some((l) => !l.geoPoint)
    )
    if (zonesToResolve.length === 0) return
    hasAutoResolved.current = true

    setIsAutoResolving(true)
    setResolveProgress({ done: 0, total: zonesToResolve.length })

    const tasks = zonesToResolve.map(async (zone) => {
      const unresolvedPrefixes = zone.locations.filter((l) => !l.geoPoint).map((l) => l.prefix)
      if (unresolvedPrefixes.length === 0) return

      try {
        const results = await resolveBatch(unresolvedPrefixes, zone.countryCode)
        setZones((prev) =>
          prev.map((z) => {
            if (z.id !== zone.id) return z
            const nextLocations = z.locations.map((loc) => {
              if (loc.geoPoint) return loc
              const idx = unresolvedPrefixes.findIndex((p) => p === loc.prefix)
              if (idx === -1) return loc
              const result = results[idx]
              if (!result) {
                return { ...loc, status: 'error' as const, resolveError: 'No response from postcode cache' }
              }
              const updated = applyGeocodeResult(
                { prefix: loc.prefix, city: loc.city, geoPoint: null },
                result
              )
              return {
                prefix: updated.prefix,
                city: updated.city,
                geoPoint: updated.geoPoint,
                status: updated.status,
                resolveError: updated.resolveError,
              }
            })
            return { ...z, locations: nextLocations }
          })
        )
      } catch (err) {
        // Contract violation (e.g. unsupported country code). Flag every unresolved location in
        // this zone with the message so the user gets actionable feedback.
        const message = err instanceof Error ? err.message : 'Postcode resolve failed'
        setZones((prev) =>
          prev.map((z) => {
            if (z.id !== zone.id) return z
            const nextLocations = z.locations.map((loc) =>
              loc.geoPoint
                ? loc
                : { ...loc, status: 'error' as const, resolveError: message }
            )
            return { ...z, locations: nextLocations }
          })
        )
      } finally {
        setResolveProgress((p) => ({ ...p, done: p.done + 1 }))
      }
    })

    void Promise.allSettled(tasks).then(() => {
      setIsAutoResolving(false)
    })
  }, [initialZones, resolveBatch])

  const handleAddZone = useCallback(() => {
    const existingNames = new Set(zones.map((z) => z.zoneName.toLowerCase()))
    let nextNum = zones.length + 1
    while (existingNames.has(`zone ${nextNum}`)) {
      nextNum++
    }
    setZones((prev) => [...prev, createEmptyZone(`Zone ${nextNum}`, services.length > 0 ? services : [DEFAULT_SERVICE], countryCode)])
    markDirty()
  }, [zones, services, countryCode, markDirty])

  const handleDeleteZone = useCallback(
    (zoneId: string) => {
      // Allow deleting the last zone — the save-time validator at "At least one zone is
      // required" still blocks committing an empty network, but the user can re-add zones
      // before saving. Until a dedicated Delete/NetworkRatecard endpoint exists, this is
      // the intended UX: visible ✕ on every row, in-state filtering works.
      setZones((prev) => prev.filter((z) => z.id !== zoneId))
      if (selectedZoneForLocation === zoneId) setSelectedZoneForLocation(null)
      if (selectedPriceCell?.zoneId === zoneId) setSelectedPriceCell(null)
      markDirty()
    },
    [selectedZoneForLocation, selectedPriceCell, markDirty]
  )

  const handleZoneNameChange = useCallback((zoneId: string, name: string) => {
    setZones((prev) => {
      const isDuplicate = prev.some((z) => z.id !== zoneId && z.zoneName.toLowerCase() === name.toLowerCase())
      if (isDuplicate) {
        setSubmitError(`Zone name "${name}" already exists. Zone names must be unique.`)
        return prev
      }
      setSubmitError(null)
      return prev.map((z) => (z.id === zoneId ? { ...z, zoneName: name } : z))
    })
    markDirty()
  }, [markDirty])

  const handleZoneMetadataChanged = useCallback(
    (zoneId: string, updates: { zoneName?: string; countryCode?: string }) => {
      setZones((prev) =>
        prev.map((z) => (z.id === zoneId ? { ...z, ...updates } : z))
      )
      markDirty()
    },
    [markDirty]
  )

  const handleAddService = useCallback(
    (service: ServiceIdentity) => {
      setZones((prev) =>
        prev.map((z) => ({
          ...z,
          servicePricing: [...z.servicePricing, createServicePricing(service)],
        }))
      )
      markDirty()
    },
    [markDirty]
  )

  const handleBreaksChange = useCallback(
    (zoneId: string, service: ServiceIdentity, breaks: QuantityBreak[]) => {
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId
            ? { ...z, servicePricing: z.servicePricing.map((sp) => (isSameService(sp, service) ? { ...sp, breaks } : sp)) }
            : z
        )
      )
      markDirty()
    },
    [markDirty]
  )

  const handleUnitValueChange = useCallback(
    (zoneId: string, service: ServiceIdentity, unitValue: number | null) => {
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId
            ? { ...z, servicePricing: z.servicePricing.map((sp) => (isSameService(sp, service) ? { ...sp, unitValue } : sp)) }
            : z
        )
      )
      markDirty()
    },
    [markDirty]
  )

  const handlePalletCapsChange = useCallback(
    (service: ServiceIdentity, palletDimensions: PalletDimensions) => {
      const lockedCaps = lockFootprint(service.palletType, palletDimensions)
      setZones((prev) =>
        prev.map((z) => ({
          ...z,
          servicePricing: z.servicePricing.map((sp) => (isSameService(sp, service) ? { ...sp, palletDimensions: lockedCaps } : sp)),
        }))
      )
      markDirty()
    },
    [markDirty]
  )

  const handleServiceChange = useCallback(
    (oldService: ServiceIdentity, newService: ServiceIdentity) => {
      if (isSameService(oldService, newService)) return
      const alreadyExists = zones.some((z) => z.servicePricing.some((sp) => isSameService(sp, newService)))
      if (alreadyExists) {
        setSubmitError(`"${getServiceLabel(newService)}" already exists. Pick a different service level or pallet type.`)
        return
      }
      setSubmitError(null)
      const palletTypeChanged = oldService.palletType !== newService.palletType || oldService.unit !== newService.unit
      setZones((prev) =>
        prev.map((z) => ({
          ...z,
          servicePricing: z.servicePricing.map((sp) => {
            if (!isSameService(sp, oldService)) return sp
            const withoutPalletFields: ZoneServicePricing = {
              serviceName: newService.serviceName,
              unit: newService.unit,
              breaks: sp.breaks,
              ...(sp.unitValue != null ? { unitValue: sp.unitValue } : {}),
            }
            if (newService.unit !== PALLET_UNIT) return withoutPalletFields
            const palletDimensions = palletTypeChanged
              ? getDefaultPalletCaps(newService.palletType) ?? sp.palletDimensions
              : sp.palletDimensions
            return {
              ...withoutPalletFields,
              ...(newService.palletType ? { palletType: newService.palletType } : {}),
              ...(palletDimensions ? { palletDimensions } : {}),
            }
          }),
        }))
      )
      setSelectedPriceCell((cell) => (cell ? { zoneId: cell.zoneId, service: newService } : cell))
      markDirty()
    },
    [zones, markDirty]
  )

  const handleRemoveService = useCallback(
    (service: ServiceIdentity) => {
      setZones((prev) => removeServiceFromEveryZone(prev, service))
      setSelectedPriceCell(null)
      markDirty()
    },
    [markDirty]
  )

  const closePanels = useCallback(() => {
    setSelectedZoneForLocation(null)
    setSelectedPriceCell(null)
    setShowAddServiceModal(false)
  }, [])

  const handleSave = async () => {
    setSubmitError(null)
    setHasTriedToSave(true)
    if (findProblemBlockingSave({ networkName, zones, surcharges })) return

    setIsSubmitting(true)

    try {
      await authHttp.post(saveEndpoint, {
        networkRateCard: {
          ...(mode === 'edit' && ratecardId ? { id: ratecardId } : {}),
          networkName: networkName.trim(),
          countryCode,
          currency,
          effectiveFrom: effectiveFrom || null,
          effectiveTo: effectiveTo || null,
          isActive,
          isLocalRatecard,
          notes: notes || null,
          zones: zones.map((zone) => ({
            zoneName: zone.zoneName,
            locations: zone.locations.map((l) => {
              // The pre-save guard above guarantees `l.geoPoint` is non-null and non-zero by the
              // time we get here. Throwing rather than defaulting to (0,0) means a regression in
              // that guard can't accidentally ship Null Island to backend.
              if (!l.geoPoint) {
                throw new Error(
                  `Internal error: postcode "${l.prefix}" reached save without a geoPoint. This should have been caught by validation.`
                )
              }
              return {
                prefix: l.prefix,
                city: l.city || '',
                geoPoint: l.geoPoint,
              }
            }),
            servicePricing: zone.servicePricing.map((s) => ({
              serviceName: s.serviceName,
              unit: s.unit,
              breaks: s.breaks.map((b) => ({
                min: b.min,
                max: b.max,
                price: b.price,
              })),
              ...(s.palletDimensions ? { palletDimensions: s.palletDimensions } : {}),
              ...(s.palletType ? { palletType: s.palletType } : {}),
              ...(s.unitValue != null ? { unitValue: s.unitValue } : {}),
            })),
          })),
          // Surcharges are network-wide. Strip the editor-only `id` before shipping to the
          // backend — the API doesn't model surcharge IDs (they're identified positionally) and
          // would otherwise complain about an unknown property.
          surcharges: surcharges
            .filter(isPricedSurcharge)
            .filter((s) => s.selectableSurchargeType)
            .map((s) => ({
              name: s.name.trim(),
              price: s.price,
              ...(s.unit?.trim() ? { unit: s.unit.trim() } : {}),
              ...(s.notes?.trim() ? { notes: s.notes.trim() } : {}),
              ...(s.selectableSurchargeType ? { selectableSurchargeType: s.selectableSurchargeType } : {}),
            })),
        },
      })

      // Save succeeded — clear the dirty flag so a subsequent Copilot import doesn't trigger the
      // "discard unsaved changes" prompt for work that's now safely persisted.
      setEditorDirty(false)
      onSaveSuccess()
    } catch (error: any) {
      console.error('Failed to save network ratecard:', error)
      setSubmitError(
        error?.response?.data?.message ||
          'Failed to save network rate card. Please check your input.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedZone = selectedZoneForLocation
    ? zones.find((z) => z.id === selectedZoneForLocation)
    : null

  const selectedPriceZone = selectedPriceCell
    ? zones.find((z) => z.id === selectedPriceCell.zoneId)
    : null

  const visibleSaveError =
    submitError ?? (hasTriedToSave ? findProblemBlockingSave({ networkName, zones, surcharges }) : null)
  const problemsToFixAfterImport =
    wingmanNotes && !isAutoResolving ? listProblemsToFixAfterImport({ networkName, zones, surcharges }) : []

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  return (
    <>
      {wingmanNotes && (
        <ImportNotesBanner
          problemsToFix={problemsToFixAfterImport}
          wingmanNotes={wingmanNotes}
          isCheckingPostcodes={isAutoResolving}
        />
      )}
      <div className='flex flex-1 overflow-hidden min-h-0'>
        <main className='flex-1 flex flex-col gap-4 p-4 lg:pt-4 lg:px-8 lg:pb-8 overflow-y-auto'>
          {/* Network Metadata */}
          <div className='bg-white border border-gray-200 rounded-lg p-5'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Network Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Network Name <span className='text-red-500'>*</span>
                </label>
                <input
                  value={networkName}
                  onChange={(e) => {
                    setNetworkName(e.target.value)
                    markDirty()
                  }}
                  className={inputClass}
                  placeholder='e.g. Palletways'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Country
                </label>
                <select
                  value={countryCode}
                  onChange={(e) => {
                    setCountryCode(e.target.value)
                    markDirty()
                  }}
                  className={inputClass}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Currency
                </label>
                <select
                  value={currency}
                  onChange={(e) => {
                    setCurrency(e.target.value)
                    markDirty()
                  }}
                  className={inputClass}
                >
                  {NETWORK_RATECARD_CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Effective From
                </label>
                <input
                  type='date'
                  value={effectiveFrom}
                  onChange={(e) => {
                    setEffectiveFrom(e.target.value)
                    markDirty()
                  }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Effective To
                </label>
                <input
                  type='date'
                  value={effectiveTo}
                  onChange={(e) => {
                    setEffectiveTo(e.target.value)
                    markDirty()
                  }}
                  className={inputClass}
                />
              </div>
            </div>
            <div className='mt-3'>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Notes
              </label>
              <input
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value)
                  markDirty()
                }}
                className={inputClass}
                placeholder='Optional notes'
              />
            </div>
            <div className='flex items-center gap-3 mt-3'>
              <button
                type='button'
                onClick={() => {
                  setIsActive(!isActive)
                  markDirty()
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isActive ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <label className='text-sm text-gray-700'>
                {isActive ? 'Active' : 'Suspended'}
              </label>
            </div>
            <div className='flex items-center gap-3 mt-3'>
              <button
                type='button'
                onClick={() => {
                  setIsLocalRatecard(!isLocalRatecard)
                  markDirty()
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isLocalRatecard ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isLocalRatecard ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <div>
                <label className='text-sm text-gray-700'>Local ratecard</label>
                {/* When on, a LOCAL origin zone is charged delivery-only (the collection leg is
                    dropped). When off (default) this is a third-party carrier ratecard: every route
                    is charged collection + delivery, regardless of origin zone. */}
                <p className='text-xs text-gray-500'>
                  Off = third-party carrier (collection + delivery on every route). On = local
                  network (LOCAL-origin routes are delivery-only).
                </p>
              </div>
            </div>
          </div>

          {/* Auto-resolve progress banner — visible only while imported postcodes are still being
              resolved against the backend cache. Shows zone-level progress (one tick per zone)
              rather than postcode-level so 1000-postcode imports don't churn the count. */}
          {isAutoResolving && (
            <div className='flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2'>
              <div className='h-2 w-2 animate-pulse rounded-full bg-blue-500' />
              <p className='text-sm text-blue-900'>
                Resolving {totalLocations} postcode
                {totalLocations !== 1 ? 's' : ''} across {resolveProgress.total} zone
                {resolveProgress.total !== 1 ? 's' : ''}…
                <span className='ml-1 text-xs text-blue-600'>
                  ({resolveProgress.done} of {resolveProgress.total} zones complete)
                </span>
              </p>
            </div>
          )}

          {/* Unresolved summary — visible after auto-resolve finishes if any locations are still
              missing a geoPoint. Save is blocked until this is empty (or the user removes the
              offending rows). The list previews the first few; full detail is in the LocationEditorPanel. */}
          {!isAutoResolving && unresolvedLocations.length > 0 && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
              <p className='text-sm font-medium text-amber-900'>
                {unresolvedLocations.length} of {totalLocations} postcode
                {totalLocations !== 1 ? 's' : ''} could not be resolved.
              </p>
              <p className='mt-1 text-xs text-amber-700'>
                Open the affected zones below to retry or remove them. Save is disabled until
                every postcode has a location.
              </p>
              <p className='mt-1 text-xs text-amber-600'>
                {unresolvedLocations.slice(0, 6).map((u) => `"${u.prefix}" in "${u.zoneName || 'Unnamed'}"`).join(', ')}
                {unresolvedLocations.length > 6 ? ` and ${unresolvedLocations.length - 6} more.` : '.'}
              </p>
            </div>
          )}

          {/* Toolbar */}
          <div className='flex items-center gap-2'>
            <button
              type='button'
              className='flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors'
              onClick={() => setShowAddServiceModal(true)}
            >
              <MdAdd className='w-4 h-4' />
              Add Service
            </button>
          </div>

          {/* Zone Grid */}
          <NetworkZoneGrid
            zones={zones}
            isAutoResolving={isAutoResolving}
            onZoneNameChange={handleZoneNameChange}
            onDeleteZone={handleDeleteZone}
            onAddZone={handleAddZone}
            onLocationCellClick={(zoneId) => {
              closePanels()
              setSelectedZoneForLocation(zoneId)
            }}
            onPriceCellClick={(zoneId, service) => {
              closePanels()
              setSelectedPriceCell({ zoneId, service })
            }}
          />

          {/* Network-wide surcharges (Saturday AM, Tail Lift, Customs Entry, Fuel Surcharge,
              etc.). Sits below the zone grid because surcharges apply on top of every zone's
              prices — visually it follows the per-zone pricing the way it follows the pricing
              tables in real UK ratecard PDFs. Currency drives the symbol prefix on each row's
              price input so users see "£75.00" rather than a unit-less number. */}
          <SurchargesPanel
            surcharges={surcharges}
            onChange={handleSurchargesChanged}
            currencyCode={currency}
          />
        </main>
      </div>

      {/* Save errors and the blocked-save hint sit outside the scroll container, directly above the
          actions ribbon, so they stay visible wherever the user is scrolled — the same slot
          FormPage gives its validation banner. */}
      {visibleSaveError && (
        <div className='px-4 pb-2 lg:px-8'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-3'>
            <p className='text-red-700 text-sm'>{visibleSaveError}</p>
          </div>
        </div>
      )}
      {(isAutoResolving || unresolvedLocations.length > 0) && !isSubmitting && (
        <div className='px-4 pb-2 lg:px-8'>
          <p className='text-xs text-gray-500'>
            {isAutoResolving
              ? 'Wait for postcode resolution to finish…'
              : 'Resolve or remove unresolved postcodes before saving.'}
          </p>
        </div>
      )}

      <FormActionsFooter
        currentStep={1}
        stepsLength={1}
        submitText={isSubmitting ? 'Saving...' : saveButtonLabel}
        onSubmit={handleSave}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        canSubmit={!isAutoResolving && unresolvedLocations.length === 0}
      />

      {/* Drawer: Location Editor */}
      {selectedZone && (
        <LocationEditorPanel
          key={selectedZone.id}
          zone={selectedZone}
          allZones={zones}
          onLocationsChanged={handleLocationsChanged}
          onZoneMetadataChanged={handleZoneMetadataChanged}
          onClose={() => setSelectedZoneForLocation(null)}
        />
      )}

      {/* Drawer: Quantity Breaks */}
      {selectedPriceZone && selectedPriceCell && (
        <QuantityBreaksPanel
          key={`${selectedPriceCell.zoneId}-${getServiceKey(selectedPriceCell.service)}`}
          zone={selectedPriceZone}
          service={selectedPriceCell.service}
          onBreaksChange={handleBreaksChange}
          onUnitValueChange={handleUnitValueChange}
          onPalletCapsChange={handlePalletCapsChange}
          onServiceChange={handleServiceChange}
          onRemoveService={handleRemoveService}
          onClose={() => setSelectedPriceCell(null)}
        />
      )}

      {/* Modal: Add Service */}
      {showAddServiceModal && (
        <AddServiceModal
          existingServices={services}
          onAdd={handleAddService}
          onClose={() => setShowAddServiceModal(false)}
        />
      )}
    </>
  )
}
